import React, { createContext, useContext, useState, ReactNode } from 'react';
import { BusinessCategory } from '../services/api';

export interface Business {
  id: string;
  name: string;
  location: string;
  email: string;
  category?: BusinessCategory;
  profileImage: string;
  status: 'active' | 'inactive';
  createdAt: Date;
  offeringsCount: number;
  redemptionsCount: number;
}

export interface User {
  id: string;
  name: string;
  email: string;
  userType: 'mobile_user' | 'business_admin';
  businessId?: string;
  businessName?: string;
  status: 'active' | 'inactive';
  joinedAt: Date;
  totalRedemptions: number;
}

export interface AdminRedemption {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  businessId: string;
  businessName: string;
  offeringName: string;
  offeringType: string;
  redeemedAt: Date;
}

interface SuperAdminContextType {
  businesses: Business[];
  users: User[];
  redemptions: AdminRedemption[];
  getRedemptionsByUser: (userId: string) => AdminRedemption[];
  updateBusinessStatus: (id: string, status: 'active' | 'inactive') => void;
  updateUserStatus: (id: string, status: 'active' | 'inactive') => void;
  addBusiness: (business: Omit<Business, 'id' | 'createdAt' | 'offeringsCount' | 'redemptionsCount' | 'status'>) => void;
}

const SuperAdminContext = createContext<SuperAdminContextType | undefined>(undefined);

// Mock data
const mockBusinesses: Business[] = [
  {
    id: '1',
    name: 'Coffee House',
    location: 'New York, NY',
    email: 'contact@coffeehouse.com',
    profileImage: '',
    status: 'active',
    createdAt: new Date('2024-01-15'),
    offeringsCount: 5,
    redemptionsCount: 45
  },
  {
    id: '2',
    name: 'Fitness Pro Gym',
    location: 'Los Angeles, CA',
    email: 'info@fitnesspro.com',
    profileImage: '',
    status: 'active',
    createdAt: new Date('2024-02-20'),
    offeringsCount: 3,
    redemptionsCount: 28
  },
  {
    id: '3',
    name: 'Tech Solutions Inc',
    location: 'San Francisco, CA',
    email: 'hello@techsolutions.com',
    profileImage: '',
    status: 'inactive',
    createdAt: new Date('2024-03-10'),
    offeringsCount: 2,
    redemptionsCount: 12
  }
];

const mockUsers: User[] = [
  {
    id: '1',
    name: 'John Doe',
    email: 'john@example.com',
    userType: 'mobile_user',
    status: 'active',
    joinedAt: new Date('2024-01-20'),
    totalRedemptions: 8
  },
  {
    id: '2',
    name: 'Jane Smith',
    email: 'jane@example.com',
    userType: 'mobile_user',
    status: 'active',
    joinedAt: new Date('2024-02-15'),
    totalRedemptions: 12
  },
  {
    id: '3',
    name: 'Bob Johnson',
    email: 'bob@example.com',
    userType: 'mobile_user',
    status: 'active',
    joinedAt: new Date('2024-03-01'),
    totalRedemptions: 5
  },
  {
    id: '4',
    name: 'Coffee House Admin',
    email: 'contact@coffeehouse.com',
    userType: 'business_admin',
    businessId: '1',
    businessName: 'Coffee House',
    status: 'active',
    joinedAt: new Date('2024-01-15'),
    totalRedemptions: 0
  },
  {
    id: '5',
    name: 'Fitness Admin',
    email: 'info@fitnesspro.com',
    userType: 'business_admin',
    businessId: '2',
    businessName: 'Fitness Pro Gym',
    status: 'active',
    joinedAt: new Date('2024-02-20'),
    totalRedemptions: 0
  }
];

const mockRedemptions: AdminRedemption[] = [
  {
    id: '1',
    userId: '1',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    businessId: '1',
    businessName: 'Coffee House',
    offeringName: 'Free Coffee',
    offeringType: 'Free Item',
    redeemedAt: new Date('2024-11-20T10:30:00')
  },
  {
    id: '2',
    userId: '1',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    businessId: '1',
    businessName: 'Coffee House',
    offeringName: '10% Discount',
    offeringType: 'Discount',
    redeemedAt: new Date('2024-11-21T14:15:00')
  },
  {
    id: '3',
    userId: '2',
    userName: 'Jane Smith',
    userEmail: 'jane@example.com',
    businessId: '2',
    businessName: 'Fitness Pro Gym',
    offeringName: 'Free Training Session',
    offeringType: 'Service',
    redeemedAt: new Date('2024-11-22T09:00:00')
  },
  {
    id: '4',
    userId: '1',
    userName: 'John Doe',
    userEmail: 'john@example.com',
    businessId: '2',
    businessName: 'Fitness Pro Gym',
    offeringName: '20% Off Membership',
    offeringType: 'Discount',
    redeemedAt: new Date('2024-11-23T16:45:00')
  },
  {
    id: '5',
    userId: '3',
    userName: 'Bob Johnson',
    userEmail: 'bob@example.com',
    businessId: '1',
    businessName: 'Coffee House',
    offeringName: 'BOGO Pastry',
    offeringType: 'BOGO',
    redeemedAt: new Date('2024-11-24T11:20:00')
  },
  {
    id: '6',
    userId: '2',
    userName: 'Jane Smith',
    userEmail: 'jane@example.com',
    businessId: '1',
    businessName: 'Coffee House',
    offeringName: 'Free Coffee',
    offeringType: 'Free Item',
    redeemedAt: new Date('2024-11-25T08:30:00')
  }
];

export const SuperAdminProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [businesses, setBusinesses] = useState<Business[]>(mockBusinesses);
  const [users, setUsers] = useState<User[]>(mockUsers);
  const [redemptions] = useState<AdminRedemption[]>(mockRedemptions);

  const getRedemptionsByUser = (userId: string): AdminRedemption[] => {
    return redemptions.filter(r => r.userId === userId);
  };

  const updateBusinessStatus = (id: string, status: 'active' | 'inactive') => {
    setBusinesses(prev => 
      prev.map(b => b.id === id ? { ...b, status } : b)
    );
  };

  const updateUserStatus = (id: string, status: 'active' | 'inactive') => {
    setUsers(prev => 
      prev.map(u => u.id === id ? { ...u, status } : u)
    );
  };

  const addBusiness = (business: Omit<Business, 'id' | 'createdAt' | 'offeringsCount' | 'redemptionsCount' | 'status'>) => {
    const newBusiness: Business = {
      ...business,
      id: Date.now().toString(),
      createdAt: new Date(),
      offeringsCount: 0,
      redemptionsCount: 0,
      status: 'active'
    };
    setBusinesses(prev => [...prev, newBusiness]);
  };

  return (
    <SuperAdminContext.Provider
      value={{
        businesses,
        users,
        redemptions,
        getRedemptionsByUser,
        updateBusinessStatus,
        updateUserStatus,
        addBusiness
      }}
    >
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => {
  const context = useContext(SuperAdminContext);
  if (context === undefined) {
    throw new Error('useSuperAdmin must be used within a SuperAdminProvider');
  }
  return context;
};

